import { Fragment, useCallback, useEffect, useState } from 'react'
import type { ApiClient, ChangePlan, RuleExplanation, RuleMode, RuleSettings, RulesView, Template } from '../api/client'
import { changeSummary } from '../i18n/derived'
import { describeError, problem, type Problem } from '../i18n/errors'
import { explained } from '../i18n/rules'
import { ProblemLine } from '../ProblemLine'
import { t, type Key } from '../i18n/dict'
import { money, pct, when } from '../i18n/format'
import { decimal } from '../i18n/locale'
import { awaitRequest } from './requests'

/**
 * Limits and rule modes for one account. Every write goes through the request queue, so the
 * time-lock applies identically here, in the TUI and in guardianctl — and the trader sees the
 * derived verdict before confirming rather than declaring their intent and being believed.
 */
export function Rules({ api, account }: { api: ApiClient; account: number }) {
  const [view, setView] = useState<RulesView | null>(null)
  const [error, setError] = useState<Problem | null>(null)
  const [editing, setEditing] = useState<{ rule: string } | 'template' | 'limits' | null>(null)
  const [catalogue, setCatalogue] = useState<Record<string, RuleExplanation>>({})
  // Which rule is opened out. One at a time: this is a 390 px sheet, not a reference manual.
  const [explaining, setExplaining] = useState<string | null>(null)

  const load = useCallback(() => {
    api.rules(account).then(setView).catch((e: unknown) => setError(describeError(e, 'error.noConfiguration')))
  }, [api, account])

  useEffect(load, [load])

  useEffect(() => {
    api
      .ruleCatalogue()
      // Translated once, here: the locale cannot change, and the explain panel should not
      // decide per render which language it is in.
      .then((list) => setCatalogue(Object.fromEntries(list.map((e) => [e.id, explained(e)]))))
      .catch(() => undefined)
  }, [api])

  if (error && !view) return <ProblemLine problem={error} />
  if (!view) return <p className="muted">{t('common.reading')}</p>

  const s = view.settings
  const rules = Object.keys(s.reactiveModes).sort()

  return (
    <section className="console">
      <dl className="grid">
        <dt>{t('rules.dt.account')}</dt>
        <dd>
          <strong>{account}</strong> <span className="muted">{t('rules.version', { n: s.version })}</span>
        </dd>
        <dt>{t('rules.dt.risk')}</dt>
        <dd>{pct(s.maxRiskPercentPerTrade)}</dd>
        <dt>{t('rules.dt.dailyLoss')}</dt>
        <dd>{money(s.dailyLossLimit)}</dd>
        <dt>{t('rules.dt.weeklyLoss')}</dt>
        <dd>{money(s.weeklyLossLimit)}</dd>
        <dt>{t('rules.dt.maxPositions')}</dt>
        <dd>{s.maxConcurrentPositions}</dd>
        <dt>{t('rules.dt.hedging')}</dt>
        <dd>{t(s.hedgingAllowed ? 'common.allowed' : 'common.blocked')}</dd>
        <dt>{t('rules.dt.tradingDay')}</dt>
        <dd>{t('rules.rollsAt', { hour: s.tradingDay.anchorHour, zone: s.tradingDay.anchorZone })}</dd>
        <dt>{t('rules.dt.relaxationDelay')}</dt>
        <dd>{s.relaxationDelay}</dd>
      </dl>

      <div className="table-wrap">
        <table className="rules">
          <thead>
            <tr>
              <th>{t('rules.th.rule')}</th>
              <th>{t('rules.th.mode')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => {
              const explained = catalogue[rule]
              const open = explaining === rule
              return (
                <Fragment key={rule}>
                  <tr className="row-link" onClick={() => setEditing({ rule })}>
                    <td className="name">
                      {explained ? <strong>{explained.title}</strong> : null}
                      <code className={explained ? 'muted' : ''}>{rule}</code>
                    </td>
                    <td>
                      <ModeTag mode={s.reactiveModes[rule]!} />
                    </td>
                    <td>
                      {explained && (
                        <button
                          type="button"
                          className="explain-toggle"
                          aria-expanded={open}
                          aria-label={t('rules.explainAria', { title: explained.title })}
                          onClick={(e) => {
                            // The row itself opens the mode editor; this must not.
                            e.stopPropagation()
                            setExplaining(open ? null : rule)
                          }}
                        >
                          {open ? '×' : '?'}
                        </button>
                      )}
                    </td>
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {explaining && catalogue[explaining] && (
        <div className="panel explain">
          <strong>{catalogue[explaining]!.title}</strong>
          <p>
            <em>{t('rules.watches')}</em> {catalogue[explaining]!.watches}
          </p>
          <p>
            <em>{t('rules.does')}</em> {catalogue[explaining]!.does}
          </p>
          <p className="muted">{catalogue[explaining]!.why}</p>
          <button type="button" onClick={() => setExplaining(null)}>
            {t('common.close')}
          </button>
        </div>
      )}

      {view.pending.length > 0 ? (
        <div className="panel">
          <strong>{t('rules.pending')}</strong>
          <ul>
            {view.pending.map((p) => (
              <li key={p.version}>
                {t('rules.pendingLine', { version: p.version, when: when(p.effectiveFrom), who: p.createdBy })}
                {p.approvedBy ? t('rules.approvedBy', { who: p.approvedBy }) : ''}
              </li>
            ))}
          </ul>
          <p className="muted">{t('rules.pendingNote')}</p>
        </div>
      ) : (
        <p className="good">{t('rules.nothingWaiting')}</p>
      )}

      <div className="actions">
        <button type="button" className="primary" onClick={() => setEditing('limits')}>
          {t('rules.changeNumbers')}
        </button>
        <button type="button" onClick={() => setEditing('template')}>
          {t('rules.applyTemplate')}
        </button>
      </div>

      {editing === 'limits' && (
        <EditLimits
          api={api}
          account={account}
          settings={s}
          onDone={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {editing !== null && editing !== 'template' && editing !== 'limits' && (
        <ChangeMode
          api={api}
          account={account}
          settings={s}
          rule={editing.rule}
          onDone={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {editing === 'template' && (
        <ApplyTemplate
          api={api}
          account={account}
          settings={s}
          onDone={() => {
            setEditing(null)
            load()
          }}
        />
      )}
    </section>
  )
}

/** The mode word is a translation, not the enum lowercased — "beobachten", not "observe". */
const MODE_LABEL: Readonly<Record<RuleMode, Key>> = {
  Observe: 'mode.observe',
  Warn: 'mode.warn',
  Enforce: 'mode.enforce',
}

const MODE_DESCRIPTION: Readonly<Record<RuleMode, Key>> = {
  Observe: 'mode.desc.Observe',
  Warn: 'mode.desc.Warn',
  Enforce: 'mode.desc.Enforce',
}

export function ModeTag({ mode }: { mode: RuleMode }) {
  return <span className={`mode ${mode.toLowerCase()}`}>{t(MODE_LABEL[mode])}</span>
}

/**
 * The numbers, edited one at a time instead of adopted wholesale.
 *
 * Until this existed the only way to change a limit was to apply a template — which sets every
 * number at once, scaled from the 1 000 the templates are written against, and is how an
 * account ends up with a daily loss of 10.78 that nobody chose.
 *
 * Nothing is derived here. The form proposes numbers; the guardian decides whether that is a
 * loosening and when it takes effect, and the pre-trade rules that test these numbers are
 * rebuilt server-side from them, so the figure shown and the figure enforced cannot part ways.
 *
 * The preview is a request, so it waits for "Review change…" rather than firing on every
 * keystroke.
 */
function EditLimits({ api, account, settings, onDone }: { api: ApiClient; account: number; settings: RuleSettings; onDone: () => void }) {
  // Label and hint are spelled out as bundle keys rather than built from the field name. A
  // template literal would need `as Key` to typecheck, and a cast here is precisely how a key
  // that does not exist reaches `t` — which throws rather than rendering.
  const fields = [
    { key: 'maxRiskPercentPerTrade', label: 'limits.maxRiskPercentPerTrade', hint: 'limits.maxRiskPercentPerTrade.hint', percent: true },
    { key: 'dailyLossLimit', label: 'limits.dailyLossLimit', hint: 'limits.dailyLossLimit.hint', percent: false },
    { key: 'weeklyLossLimit', label: 'limits.weeklyLossLimit', hint: 'limits.weeklyLossLimit.hint', percent: false },
    { key: 'maxConcurrentPositions', label: 'limits.maxConcurrentPositions', hint: 'limits.maxConcurrentPositions.hint', percent: false },
    { key: 'maxOpenRiskPercent', label: 'limits.maxOpenRiskPercent', hint: 'limits.maxOpenRiskPercent.hint', percent: true },
    { key: 'maxDrawdownPercent', label: 'limits.maxDrawdownPercent', hint: 'limits.maxDrawdownPercent.hint', percent: true },
    { key: 'maxStopDistancePercent', label: 'limits.maxStopDistancePercent', hint: 'limits.maxStopDistancePercent.hint', percent: true },
    { key: 'minMarginLevel', label: 'limits.minMarginLevel', hint: 'limits.minMarginLevel.hint', percent: true },
  ] as const satisfies ReadonlyArray<{ key: keyof RuleSettings; label: Key; hint: Key; percent: boolean }>

  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, String(settings[f.key])])),
  )
  const [hedging, setHedging] = useState(settings.hedgingAllowed)
  const [reviewing, setReviewing] = useState<RuleSettings | null>(null)

  const numbers = fields.map((f) => [f.key, decimal(text[f.key] ?? '')] as const)
  const bad = numbers.filter(([, v]) => !Number.isFinite(v) || v < 0).map(([k]) => k)
  const positionsWhole = Number.isInteger(decimal(text['maxConcurrentPositions'] ?? ''))

  const build = (): RuleSettings => ({
    ...settings,
    ...Object.fromEntries(numbers),
    maxConcurrentPositions: Math.trunc(decimal(text['maxConcurrentPositions'] ?? '')),
    hedgingAllowed: hedging,
  })

  if (reviewing) {
    return (
      <div className="panel">
        <SaveChange api={api} account={account} proposed={reviewing} what={t('limits.title')} onDone={onDone} />
        <button type="button" onClick={() => setReviewing(null)}>
          {t('limits.backToNumbers')}
        </button>
      </div>
    )
  }

  return (
    <div className="panel">
      <strong>{t('limits.title')}</strong>
      <div className="fields">
        {fields.map((f) => {
          const hint = t(f.hint)
          return (
            <label key={f.key}>
              <span>{t(f.label)}</span>
              <span className="stop-input">
                <input
                  inputMode="decimal"
                  value={text[f.key] ?? ''}
                  aria-invalid={bad.includes(f.key)}
                  onChange={(e) => setText({ ...text, [f.key]: e.target.value })}
                />
                {f.percent ? <span className="unit static">{t('unit.percent')}</span> : null}
              </span>
              {hint ? <small className="muted">{hint}</small> : null}
            </label>
          )
        })}
        <label>
          <span>{t('limits.hedging')}</span>
          <span>
            <input type="checkbox" checked={hedging} onChange={(e) => setHedging(e.target.checked)} />{' '}
            {t('common.allowed')}
          </span>
        </label>
      </div>

      {bad.length > 0 && (
        <p className="bad">
          {t('limits.notNumbers', {
            fields: bad.map((k) => t(fields.find((f) => f.key === k)!.label)).join(', '),
          })}
        </p>
      )}
      {bad.length === 0 && !positionsWhole && <p className="bad">{t('limits.wholeNumber')}</p>}

      <p className="muted">{t('limits.note')}</p>

      <div className="actions">
        <button type="button" className="primary" disabled={bad.length > 0 || !positionsWhole} onClick={() => setReviewing(build())}>
          {t('limits.review')}
        </button>
        <button type="button" onClick={onDone}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}

/**
 * The Phase 2 rollout operation: graduate one rule, on one account. The rule was chosen from a
 * list rather than typed, because a typo in a rule id costs a silently unenforced rule.
 */
function ChangeMode({ api, account, settings, rule, onDone }: { api: ApiClient; account: number; settings: RuleSettings; rule: string; onDone: () => void }) {
  const [mode, setMode] = useState<RuleMode>(settings.reactiveModes[rule] ?? 'Observe')
  const proposed: RuleSettings = { ...settings, reactiveModes: { ...settings.reactiveModes, [rule]: mode } }
  return (
    <div className="panel">
      <p>{t('mode.forRule', { rule })}</p>
      <div className="choices">
        {(['Observe', 'Warn', 'Enforce'] as RuleMode[]).map((m) => (
          <label key={m}>
            <input type="radio" name="mode" checked={mode === m} onChange={() => setMode(m)} />
            <ModeTag mode={m} /> {t(MODE_DESCRIPTION[m])}
          </label>
        ))}
      </div>
      {mode === 'Enforce' && mode !== settings.reactiveModes[rule] && (
        <div className="panel red">
          <strong>{t('mode.willAct.title')}</strong>
          <p>{t('mode.willAct.body', { rule, account })}</p>
          <p className="muted">{t('mode.willAct.note')}</p>
        </div>
      )}
      {mode === settings.reactiveModes[rule] ? (
        <p className="muted">{t('mode.already', { mode: t(MODE_LABEL[mode]) })}</p>
      ) : (
        <SaveChange
          api={api}
          account={account}
          proposed={proposed}
          what={t('mode.what', { rule, mode: t(MODE_LABEL[mode]) })}
          onDone={onDone}
        />
      )}
      <button type="button" onClick={onDone}>
        {t('common.back')}
      </button>
    </div>
  )
}

function ApplyTemplate({ api, account, settings, onDone }: { api: ApiClient; account: number; settings: RuleSettings; onDone: () => void }) {
  const [templates, setTemplates] = useState<Template[]>([])
  const [name, setName] = useState('')
  const [equity, setEquity] = useState('')

  useEffect(() => {
    api.templates().then(setTemplates).catch(() => setTemplates([]))
  }, [api])

  const chosen = templates.find((t) => t.name === name)
  // Scaled like RuleTemplates.ForEquity: the loss limits follow the equity, the ratios do not.
  const scale = chosen && decimal(equity) > 0 ? decimal(equity) / 1000 : 1
  const proposed: RuleSettings | null = chosen
    ? {
        ...settings,
        maxRiskPercentPerTrade: chosen.maxRiskPercentPerTrade,
        maxConcurrentPositions: chosen.maxConcurrentPositions,
        maxOpenRiskPercent: chosen.maxOpenRiskPercent,
        maxDrawdownPercent: chosen.maxDrawdownPercent,
        hedgingAllowed: chosen.hedgingAllowed,
        dailyLossLimit: Number((chosen.dailyLossLimit * scale).toFixed(2)),
        weeklyLossLimit: Number((chosen.weeklyLossLimit * scale).toFixed(2)),
      }
    : null

  return (
    <div className="panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('tmpl.th.template')}</th>
              <th>{t('tmpl.th.risk')}</th>
              <th>{t('tmpl.th.positions')}</th>
              <th>{t('tmpl.th.openRisk')}</th>
              <th>{t('tmpl.th.drawdown')}</th>
              <th>{t('tmpl.th.hedging')}</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((row) => (
              <tr key={row.name} className={name === row.name ? 'row-link on' : 'row-link'} onClick={() => setName(row.name)}>
                <td>{row.name}</td>
                <td>{pct(row.maxRiskPercentPerTrade)}</td>
                <td>{row.maxConcurrentPositions}</td>
                <td>{pct(row.maxOpenRiskPercent)}</td>
                <td>{pct(row.maxDrawdownPercent)}</td>
                <td>{t(row.hedgingAllowed ? 'common.allowed' : 'common.blocked')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="inline">
        <span>{t('template.equityQuestion')}</span>
        <input inputMode="decimal" value={equity} onChange={(e) => setEquity(e.target.value)} placeholder="1000" />
      </label>
      {proposed && (
        <SaveChange api={api} account={account} proposed={proposed} what={t('template.what', { name })} onDone={onDone} />
      )}
      <button type="button" onClick={onDone}>
        {t('common.back')}
      </button>
    </div>
  )
}

/**
 * The verdict is rendered from the server's plan — a pure read under this trader's role — so
 * what the confirmation shows is what the daemon will derive. The write itself goes through
 * the request queue; this login cannot touch rule_config.
 */
function SaveChange({ api, account, proposed, what, onDone }: { api: ApiClient; account: number; proposed: RuleSettings; what: string; onDone: () => void }) {
  const [plan, setPlan] = useState<ChangePlan | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Problem | null>(null)

  useEffect(() => {
    let cancelled = false
    setPlan(null)
    api
      .previewChange(account, proposed)
      .then((p) => !cancelled && setPlan(p))
      .catch((e: unknown) => !cancelled && setOutcome(describeError(e, 'error.noConfiguration')))
    return () => {
      cancelled = true
    }
  }, [api, account, proposed])

  const save = async () => {
    setBusy(t('save.writing'))
    try {
      const submitted = await api.submitChange(account, proposed)
      const result = await awaitRequest(api, submitted.id)
      if (result.status === 'failed') {
        // The daemon's words, verbatim. English by decision (docs/12-plugin-i18n.md §8): it is
        // written by a process handling a queue row, with no request to read a language from.
        setOutcome(problem(t('requests.failed'), result.error))
        return
      }
      const r = result.result ?? {}
      const version = String(r['version'])
      setOutcome(
        problem(
          r['is_relaxation'] === true
            ? t('save.savedDelayed', { version, when: when(String(r['effective_from'])) })
            : t('save.saved', { version }),
        ),
      )
    } finally {
      setBusy(null)
    }
  }

  if (outcome) {
    return (
      <div className="panel">
        <ProblemLine problem={outcome} className="" />
        <button type="button" onClick={onDone}>
          {t('common.ok')}
        </button>
      </div>
    )
  }

  if (!plan) return <p className="muted">{t('save.deriving')}</p>

  return (
    <div className={plan.isRelaxation ? 'panel yellow' : 'panel'}>
      {plan.isRelaxation ? (
        <>
          <strong>{t('save.timeLock')}</strong>
          <p className="warn">{t('save.loosens')}</p>
          {/* plan.reasons stays English: translating it means changing what
              SettingsComparison.Relaxations returns, and these read as field-name-plus-arrow.
              docs/12-plugin-i18n.md §8 says why that is not worth doing yet. */}
          <ul>
            {plan.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <p>{changeSummary(plan)}</p>
          <p className="muted">{t('save.delayNote')}</p>
        </>
      ) : (
        <p className="good">{changeSummary(plan)}</p>
      )}
      {busy && <p className="muted">{busy}</p>}
      <button type="button" className="primary" disabled={busy !== null} onClick={() => void save()}>
        {t('save.button', { what })}
      </button>
    </div>
  )
}
